import { useEffect, useState } from "react";
import { Users, BookOpen, Plus, Trash2, Upload, FileText, Power, Database, Image as ImageIcon, FileType2, Lightbulb, Check, X, Sparkles, RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { UsersTab } from "../components/admin/UsersTab";
import { UserManageModal } from "../components/admin/UserManageModal";
import { HardDeleteModal } from "../components/admin/HardDeleteModal";

const CUSTOMER_TYPES = ["skeptisch", "dominant", "technisch", "emotional", "preisorientiert", "misstrauisch", "freundlich"];

const Admin = () => {
  const { user: currentUser } = useAuth();
  const [tab, setTab] = useState(() => {
    try {
      const requested = sessionStorage.getItem("admin_open_tab");
      if (requested) {
        sessionStorage.removeItem("admin_open_tab");
        return requested;
      }
    } catch (e) { /* noop */ }
    return "scenarios";
  });
  const [scenarios, setScenarios] = useState([]);
  const [users, setUsers] = useState([]);
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [manageUser, setManageUser] = useState(null); // user object opened in mgmt modal
  const [hardDeleteFor, setHardDeleteFor] = useState(null); // user object pending hard-delete confirm
  const [knowledge, setKnowledge] = useState([]);

  const [newScenario, setNewScenario] = useState({
    title: "", description: "", difficulty: "mittel", customer_type: "skeptisch", learning_goal: "", typical_objections: ""
  });
  const [newKnowledge, setNewKnowledge] = useState({ title: "", category: "Verhalten", content: "" });
  const [docFile, setDocFile] = useState(null);
  const [docMeta, setDocMeta] = useState({ title: "", category: "Wissen" });
  const [imgFile, setImgFile] = useState(null);
  const [imgMeta, setImgMeta] = useState({ title: "", category: "Wissen" });
  const [uploading, setUploading] = useState(false);
  const [insights, setInsights] = useState([]);
  const [insightsStatus, setInsightsStatus] = useState(null);
  const [generatingInsights, setGeneratingInsights] = useState(false);

  const loadInsights = async () => {
    try {
      const [list, st] = await Promise.all([
        api.get("/admin/insights"),
        api.get("/admin/insights/status"),
      ]);
      setInsights(list.data || []);
      setInsightsStatus(st.data || null);
    } catch (e) { /* admin-only — fail silently for non-admin loads */ }
  };

  const generateInsights = async () => {
    setGeneratingInsights(true);
    try {
      const { data } = await api.post("/admin/insights/generate");
      if (data.ok) {
        toast.success(`${data.team_snippets} Team-Snippets + ${data.personal_tips} persönliche Tipps generiert`);
      } else {
        toast.info(`Zu wenige Daten (${data.feedbacks_analyzed}/${data.min_required} Trainings)`);
      }
      await loadInsights();
    } catch (e) { toast.error(e?.response?.data?.detail || "Generierung fehlgeschlagen"); }
    finally { setGeneratingInsights(false); }
  };

  const approveInsight = async (id) => {
    try {
      const { data } = await api.post(`/admin/insights/${id}/approve`);
      toast.success(data.type === "kb_snippet" ? "In Wissensbasis übernommen" : "Persönlicher Tipp gespeichert");
      loadInsights(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Übernahme fehlgeschlagen"); }
  };

  const rejectInsight = async (id) => {
    try {
      await api.post(`/admin/insights/${id}/reject`);
      toast.success("Verworfen");
      loadInsights();
    } catch (e) { toast.error("Fehler"); }
  };

  const load = async () => {
    try {
      const [sc, us, kn] = await Promise.all([
        api.get("/scenarios"), api.get("/admin/users"), api.get("/admin/knowledge"),
      ]);
      setScenarios(sc.data); setUsers(us.data); setKnowledge(kn.data);
    } catch (e) { toast.error("Konnte Admin-Daten nicht laden"); }
  };

  const loadDeletedUsers = async () => {
    try {
      const { data } = await api.get("/admin/users/deleted");
      setDeletedUsers(data || []);
    } catch (e) {
      // Only direktoren/admins can read this — silently swallow 401/403 (e.g.,
      // direktor without admin role getting filtered) but surface real 5xx
      // failures so an outage isn't masked.
      const status = e?.response?.status;
      if (status && status >= 500) {
        toast.error("Gelöschte Nutzer konnten nicht geladen werden");
      }
    }
  };

  const softDeleteUser = async (u) => {
    if (!window.confirm(
      `Möchtest du ${u.first_name} ${u.last_name} wirklich anonymisieren?\n\n` +
      `• Name & E-Mail werden geschwärzt\n` +
      `• Login ist sofort gesperrt\n` +
      `• Trainings-Daten bleiben für die Compliance-Aufbewahrung erhalten\n` +
      `• Wiederherstellung jederzeit möglich`
    )) return;
    try {
      const { data } = await api.delete(`/admin/users/${u.id}`);
      toast.success(data.message || "Nutzer anonymisiert");
      setManageUser(null);
      await Promise.all([load(), loadDeletedUsers()]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen"); }
  };

  const hardDeleteUser = async (u) => {
    try {
      const { data } = await api.delete(`/admin/users/${u.id}?hard=true`);
      const d = data.deleted || {};
      toast.success(
        `Endgültig gelöscht: ${d.user || 1} Nutzer, ${d.sessions || 0} Sessions, ` +
        `${d.messages || 0} Nachrichten, ${d.s3_recordings || 0} Audio-Dateien`
      );
      setHardDeleteFor(null);
      setManageUser(null);
      await Promise.all([load(), loadDeletedUsers()]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Hard-Delete fehlgeschlagen"); }
  };

  const restoreUser = async (u) => {
    if (!window.confirm(`${u.original_first_name || u.first_name} ${u.original_last_name || u.last_name} wirklich wiederherstellen?`)) return;
    try {
      await api.post(`/admin/users/${u.id}/restore`);
      toast.success("Nutzer wiederhergestellt");
      await Promise.all([load(), loadDeletedUsers()]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Wiederherstellung fehlgeschlagen"); }
  };

  useEffect(() => { load(); loadInsights(); loadDeletedUsers(); }, []);

  const createScenario = async () => {
    if (!newScenario.title || !newScenario.description || !newScenario.learning_goal) {
      return toast.error("Bitte Titel, Beschreibung und Lernziel ausfüllen");
    }
    try {
      const payload = { ...newScenario, typical_objections: newScenario.typical_objections.split("\n").filter(Boolean) };
      await api.post("/scenarios", payload);
      toast.success("Szenario erstellt");
      setNewScenario({ title: "", description: "", difficulty: "mittel", customer_type: "skeptisch", learning_goal: "", typical_objections: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  const deleteScenario = async (id) => {
    if (!window.confirm("Szenario wirklich löschen?")) return;
    await api.delete(`/scenarios/${id}`); toast.success("Gelöscht"); load();
  };

  const addKnowledge = async () => {
    if (!newKnowledge.title || !newKnowledge.content) return toast.error("Bitte alle Felder ausfüllen");
    await api.post("/admin/knowledge", newKnowledge);
    toast.success("Eintrag erstellt");
    setNewKnowledge({ title: "", category: "Verhalten", content: "" }); load();
  };

  const uploadDoc = async () => {
    if (!docFile || !docMeta.title) return toast.error("Datei und Titel erforderlich");
    setUploading(true);
    const form = new FormData();
    form.append("title", docMeta.title);
    form.append("category", docMeta.category);
    form.append("file", docFile);
    try {
      await api.post("/admin/knowledge/upload-document", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Dokument verarbeitet & aktiv");
      setDocFile(null); setDocMeta({ title: "", category: "Wissen" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload fehlgeschlagen"); }
    finally { setUploading(false); }
  };

  const uploadImg = async () => {
    if (!imgFile || !imgMeta.title) return toast.error("Bild und Titel erforderlich");
    setUploading(true);
    const form = new FormData();
    form.append("title", imgMeta.title);
    form.append("category", imgMeta.category);
    form.append("file", imgFile);
    try {
      await api.post("/admin/knowledge/upload-image", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Bild analysiert & in Wissensbasis übernommen");
      setImgFile(null); setImgMeta({ title: "", category: "Wissen" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "OCR fehlgeschlagen"); }
    finally { setUploading(false); }
  };

  const toggleKnowledge = async (id) => { await api.post(`/admin/knowledge/${id}/toggle`); load(); };
  const deleteKnowledge = async (id) => {
    if (!window.confirm("Eintrag löschen?")) return;
    await api.delete(`/admin/knowledge/${id}`); toast.success("Gelöscht"); load();
  };

  const TABS = [
    { key: "scenarios", label: "Szenarien", icon: BookOpen },
    { key: "users", label: "Nutzer", icon: Users },
    { key: "knowledge", label: "KI-Wissensbasis", icon: Database },
    { key: "insights", label: "Auto-Insights", icon: Lightbulb, badge: insights.filter(i => i.status === "pending").length },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
      <div className="animate-slide-up">
        <span className="label-eyebrow">Admin-Bereich</span>
        <h1 className="font-display font-black text-3xl sm:text-5xl tracking-tight text-white mt-2">
          Steuerung & <span className="text-novatus-green">KI-Training.</span>
        </h1>
        <p className="text-novatus-text-2 mt-3 max-w-2xl">Verwalte Szenarien, Nutzer und schule die KI mit eigener Wissensbasis.</p>
      </div>

      <div className="mt-8 flex flex-wrap gap-2">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} className={`pill-tab ${tab === t.key ? "active" : ""}`} data-testid={`tab-${t.key}`}>
              <Icon className="w-4 h-4" /> {t.label}
              {t.badge > 0 && (
                <span className="ml-1 text-[10px] font-black px-1.5 py-0.5 rounded-full bg-amber-400/20 text-amber-300 border border-amber-400/40" data-testid={`tab-${t.key}-badge`}>
                  {t.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* SCENARIOS */}
      {tab === "scenarios" && (
        <div className="mt-6 space-y-5">
          <div className="nv-card p-6">
            <h3 className="font-display font-bold text-lg text-white">Neues Szenario</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="md:col-span-2">
                <label className="label-eyebrow block mb-1.5">Titel</label>
                <input value={newScenario.title} onChange={e => setNewScenario({...newScenario, title: e.target.value})} className="nv-input w-full" data-testid="new-scenario-title" />
              </div>
              <div className="md:col-span-2">
                <label className="label-eyebrow block mb-1.5">Beschreibung</label>
                <textarea rows={2} value={newScenario.description} onChange={e => setNewScenario({...newScenario, description: e.target.value})} className="nv-input w-full" data-testid="new-scenario-desc" />
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Schwierigkeit</label>
                <select value={newScenario.difficulty} onChange={e => setNewScenario({...newScenario, difficulty: e.target.value})} className="nv-input w-full" data-testid="new-scenario-difficulty">
                  <option value="leicht">leicht</option><option value="mittel">mittel</option><option value="schwer">schwer</option>
                </select>
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Kundentyp</label>
                <select value={newScenario.customer_type} onChange={e => setNewScenario({...newScenario, customer_type: e.target.value})} className="nv-input w-full" data-testid="new-scenario-ctype">
                  {CUSTOMER_TYPES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="label-eyebrow block mb-1.5">Lernziel</label>
                <input value={newScenario.learning_goal} onChange={e => setNewScenario({...newScenario, learning_goal: e.target.value})} className="nv-input w-full" data-testid="new-scenario-goal" />
              </div>
              <div className="md:col-span-2">
                <label className="label-eyebrow block mb-1.5">Typische Einwände (1 pro Zeile)</label>
                <textarea rows={3} value={newScenario.typical_objections} onChange={e => setNewScenario({...newScenario, typical_objections: e.target.value})} className="nv-input w-full" data-testid="new-scenario-objections" />
              </div>
            </div>
            <button onClick={createScenario} className="btn-green mt-4 h-10 px-5 rounded-lg flex items-center gap-2" data-testid="create-scenario-btn">
              <Plus className="w-4 h-4" /> Szenario anlegen
            </button>
          </div>

          <div className="nv-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-novatus-card-2 border-b border-novatus">
                <tr className="text-left">
                  <th className="px-4 py-3 label-eyebrow">Titel</th>
                  <th className="px-4 py-3 label-eyebrow">Schwierigkeit</th>
                  <th className="px-4 py-3 label-eyebrow">Kundentyp</th>
                  <th className="px-4 py-3 label-eyebrow">Lernziel</th>
                  <th className="px-4 py-3 label-eyebrow text-right">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {scenarios.map(s => (
                  <tr key={s.id} className="border-b border-novatus hover:bg-white/[0.02]" data-testid={`scenario-row-${s.id}`}>
                    <td className="px-4 py-3 font-semibold text-white">{s.title}</td>
                    <td className="px-4 py-3"><span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full diff-${s.difficulty}`}>{s.difficulty}</span></td>
                    <td className="px-4 py-3 text-novatus-text-2">{s.customer_type}</td>
                    <td className="px-4 py-3 text-novatus-text-2 max-w-xs truncate">{s.learning_goal}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => deleteScenario(s.id)} className="p-2 hover:bg-red-500/10 rounded-md" data-testid={`delete-scenario-${s.id}`}>
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* USERS */}
      {tab === "users" && (
        <UsersTab
          users={users}
          deletedUsers={deletedUsers}
          currentUser={currentUser}
          onManage={(u) => setManageUser(u)}
          onSoftDelete={softDeleteUser}
          onRestore={restoreUser}
          onHardDelete={(u) => setHardDeleteFor(u)}
        />
      )}

      {/* KNOWLEDGE */}
      {tab === "knowledge" && (
        <div className="mt-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* Manual entry */}
            <div className="nv-card p-6">
              <h3 className="font-display font-bold text-lg text-white flex items-center gap-2">
                <Plus className="w-4 h-4 text-novatus-green" /> Manuell hinzufügen
              </h3>
              <p className="text-xs text-novatus-text-3 mt-1">Fließt direkt in den System-Prompt des KI-Kunden ein.</p>
              <div className="space-y-3 mt-4">
                <input placeholder="Titel" value={newKnowledge.title} onChange={e => setNewKnowledge({...newKnowledge, title: e.target.value})} className="nv-input w-full" data-testid="kb-title" />
                <input placeholder="Kategorie" value={newKnowledge.category} onChange={e => setNewKnowledge({...newKnowledge, category: e.target.value})} className="nv-input w-full" data-testid="kb-category" />
                <textarea rows={5} placeholder="Verhaltensanweisung / Wissen..." value={newKnowledge.content} onChange={e => setNewKnowledge({...newKnowledge, content: e.target.value})} className="nv-input w-full" data-testid="kb-content" />
                <button onClick={addKnowledge} className="btn-green w-full h-10 rounded-lg" data-testid="kb-add-btn">Hinzufügen</button>
              </div>
            </div>

            {/* Document upload */}
            <div className="nv-card p-6">
              <h3 className="font-display font-bold text-lg text-white flex items-center gap-2">
                <FileType2 className="w-4 h-4 text-novatus-green" /> Dokument hochladen
              </h3>
              <p className="text-xs text-novatus-text-3 mt-1">PDF, DOCX, TXT, MD, CSV — Text wird extrahiert.</p>
              <div className="space-y-3 mt-4">
                <input placeholder="Titel" value={docMeta.title} onChange={e => setDocMeta({...docMeta, title: e.target.value})} className="nv-input w-full" data-testid="doc-title" />
                <input placeholder="Kategorie" value={docMeta.category} onChange={e => setDocMeta({...docMeta, category: e.target.value})} className="nv-input w-full" data-testid="doc-category" />
                <label className="flex flex-col items-center justify-center gap-2 h-24 border-2 border-dashed border-novatus-hi rounded-lg cursor-pointer hover:border-novatus-green/50 transition-colors">
                  <input type="file" accept=".pdf,.docx,.txt,.md,.csv" onChange={e => setDocFile(e.target.files?.[0] || null)} className="hidden" data-testid="doc-file" />
                  <Upload className="w-5 h-5 text-novatus-text-2" />
                  <span className="text-xs text-novatus-text-2">{docFile ? `${docFile.name} (${Math.round(docFile.size/1024)} KB)` : "Klicken oder Datei ablegen"}</span>
                </label>
                <button onClick={uploadDoc} disabled={!docFile || uploading} className="btn-green w-full h-10 rounded-lg disabled:opacity-50" data-testid="doc-upload-btn">
                  {uploading ? "Verarbeite..." : "Hochladen & analysieren"}
                </button>
              </div>
            </div>

            {/* Image OCR upload */}
            <div className="nv-card p-6">
              <h3 className="font-display font-bold text-lg text-white flex items-center gap-2">
                <ImageIcon className="w-4 h-4 text-novatus-green" /> Bild (KI-OCR)
              </h3>
              <p className="text-xs text-novatus-text-3 mt-1">Foto eines Dokuments, Etiketts oder Handbuchs — GPT-4o-mini Vision liest und versteht.</p>
              <div className="space-y-3 mt-4">
                <input placeholder="Titel" value={imgMeta.title} onChange={e => setImgMeta({...imgMeta, title: e.target.value})} className="nv-input w-full" data-testid="img-title" />
                <input placeholder="Kategorie" value={imgMeta.category} onChange={e => setImgMeta({...imgMeta, category: e.target.value})} className="nv-input w-full" data-testid="img-category" />
                <label className="flex flex-col items-center justify-center gap-2 h-24 border-2 border-dashed border-novatus-hi rounded-lg cursor-pointer hover:border-novatus-green/50 transition-colors">
                  <input type="file" accept="image/*" onChange={e => setImgFile(e.target.files?.[0] || null)} className="hidden" data-testid="img-file" />
                  <ImageIcon className="w-5 h-5 text-novatus-text-2" />
                  <span className="text-xs text-novatus-text-2">{imgFile ? `${imgFile.name} (${Math.round(imgFile.size/1024)} KB)` : "Bild auswählen (jpg/png)"}</span>
                </label>
                <button onClick={uploadImg} disabled={!imgFile || uploading} className="btn-green w-full h-10 rounded-lg disabled:opacity-50" data-testid="img-upload-btn">
                  {uploading ? "Analysiere..." : "Bild analysieren"}
                </button>
              </div>
            </div>
          </div>

          <div className="nv-card overflow-hidden">
            <div className="p-4 border-b border-novatus flex items-center gap-2">
              <Database className="w-4 h-4 text-novatus-green" />
              <h3 className="font-display font-bold text-white">Aktive KI-Wissensbasis ({knowledge.filter(k => k.active).length} aktiv von {knowledge.length})</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-novatus-card-2 border-b border-novatus">
                  <tr className="text-left">
                    <th className="px-4 py-3 label-eyebrow">Titel</th>
                    <th className="px-4 py-3 label-eyebrow">Kategorie</th>
                    <th className="px-4 py-3 label-eyebrow">Quelle</th>
                    <th className="px-4 py-3 label-eyebrow">Auszug</th>
                    <th className="px-4 py-3 label-eyebrow">Status</th>
                    <th className="px-4 py-3 label-eyebrow text-right">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {knowledge.map(k => (
                    <tr key={k.id} className="border-b border-novatus hover:bg-white/[0.02]" data-testid={`kb-row-${k.id}`}>
                      <td className="px-4 py-3 font-semibold text-white">
                        <div className="flex items-center gap-2">
                          {k.source_type === "image_ocr" ? <ImageIcon className="w-3 h-3 text-novatus-green" /> :
                           k.source_type === "document" ? <FileText className="w-3 h-3 text-novatus-text-3" /> : null}
                          {k.title}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-novatus-text-2">{k.category}</td>
                      <td className="px-4 py-3 text-xs text-novatus-text-3">{k.source_type === "image_ocr" ? "Vision-OCR" : k.source_type === "document" ? (k.filename || "Dokument") : "Manuell"}</td>
                      <td className="px-4 py-3 text-novatus-text-2 max-w-xs truncate">{k.content?.substring(0, 100)}...</td>
                      <td className="px-4 py-3">
                        {k.active
                          ? <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-novatus-green/15 text-novatus-green border border-novatus-green/40">aktiv</span>
                          : <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-novatus-card-2 border border-novatus text-novatus-text-3">inaktiv</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => toggleKnowledge(k.id)} className="p-2 hover:bg-white/5 rounded-md" data-testid={`kb-toggle-${k.id}`}>
                          <Power className="w-4 h-4 text-novatus-text-2" />
                        </button>
                        <button onClick={() => deleteKnowledge(k.id)} className="p-2 hover:bg-red-500/10 rounded-md" data-testid={`kb-delete-${k.id}`}>
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {/* INSIGHTS */}
      {tab === "insights" && (
        <div className="mt-6 space-y-5" data-testid="insights-section">
          <div className="nv-card p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-novatus-green/10 border border-novatus-green/40 flex items-center justify-center shrink-0">
                  <Sparkles className="w-5 h-5 text-novatus-green" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-lg text-white">Auto-Insight-Engine</h3>
                  <p className="text-sm text-novatus-text-2 mt-1 max-w-2xl">
                    Die KI analysiert alle bisherigen Trainings, erkennt wiederkehrende Schwächen
                    und schlägt sofort einsetzbare Wissens-Snippets und persönliche Coaching-Tipps vor.
                    Auto-Lauf alle 50 Trainings.
                  </p>
                  {insightsStatus && (
                    <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-xs text-novatus-text-3">
                      <span>Abgeschlossene Trainings: <span className="text-white font-mono">{insightsStatus.total_completed_sessions}</span></span>
                      <span>Nächster Auto-Lauf bei: <span className="text-white font-mono">{insightsStatus.next_auto_run_at_sessions}</span></span>
                      {insightsStatus.last_run && (
                        <span>Letzter Lauf: <span className="text-white">{new Date(insightsStatus.last_run.at).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}</span></span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={generateInsights}
                disabled={generatingInsights}
                className="btn-green h-11 px-5 rounded-lg flex items-center gap-2 disabled:opacity-50 shrink-0"
                data-testid="generate-insights-btn"
              >
                <RefreshCw className={`w-4 h-4 ${generatingInsights ? "animate-spin" : ""}`} />
                {generatingInsights ? "Analysiere..." : "Jetzt analysieren"}
              </button>
            </div>
          </div>

          {/* Pending suggestions */}
          {insights.filter(i => i.status === "pending").length === 0 ? (
            <div className="nv-card p-12 text-center">
              <Lightbulb className="w-10 h-10 text-novatus-text-3 mx-auto mb-3" />
              <p className="text-novatus-text-2">Keine neuen Vorschläge bereit.</p>
              <p className="text-xs text-novatus-text-3 mt-1">
                Klicke „Jetzt analysieren" oder warte bis zum nächsten Auto-Lauf.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {insights.filter(i => i.status === "pending").map(ins => (
                <div key={ins.id} className="nv-card p-5 nv-card-hover" data-testid={`insight-${ins.id}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                      ins.type === "kb_snippet"
                        ? "bg-novatus-green/10 border border-novatus-green/40 text-novatus-green"
                        : "bg-blue-400/10 border border-blue-400/40 text-blue-300"
                    }`}>
                      {ins.type === "kb_snippet" ? <Database className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${
                          ins.type === "kb_snippet"
                            ? "bg-novatus-green/15 text-novatus-green border border-novatus-green/40"
                            : "bg-blue-400/15 text-blue-300 border border-blue-400/40"
                        }`}>
                          {ins.type === "kb_snippet" ? "Team-Wissen" : `Persönlich · ${ins.target_user_name}`}
                        </span>
                        <span className="text-[9px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-novatus-card-2 border border-novatus text-novatus-text-2">
                          {ins.category}
                        </span>
                      </div>
                      <h4 className="font-display font-bold text-white">{ins.title}</h4>
                      <p className="text-sm text-novatus-text-2 mt-2 whitespace-pre-wrap">{ins.content}</p>
                      {ins.rationale && (
                        <p className="text-xs text-novatus-text-3 mt-2 italic">→ {ins.rationale}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end mt-4 pt-3 border-t border-novatus">
                    <button
                      onClick={() => rejectInsight(ins.id)}
                      className="h-9 px-3 rounded-lg text-xs text-novatus-text-2 hover:text-red-400 hover:bg-red-400/10 inline-flex items-center gap-1.5"
                      data-testid={`reject-insight-${ins.id}`}
                    >
                      <X className="w-3.5 h-3.5" /> Verwerfen
                    </button>
                    <button
                      onClick={() => approveInsight(ins.id)}
                      className="btn-green h-9 px-4 rounded-lg text-xs inline-flex items-center gap-1.5"
                      data-testid={`approve-insight-${ins.id}`}
                    >
                      <Check className="w-3.5 h-3.5" /> Übernehmen
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Approved history */}
          {insights.filter(i => i.status === "approved").length > 0 && (
            <div className="nv-card overflow-hidden">
              <div className="p-4 border-b border-novatus flex items-center gap-2">
                <Check className="w-4 h-4 text-novatus-green" />
                <h3 className="font-display font-bold text-white">Bereits übernommen ({insights.filter(i => i.status === "approved").length})</h3>
              </div>
              <div className="divide-y divide-novatus max-h-64 overflow-y-auto">
                {insights.filter(i => i.status === "approved").slice(0, 20).map(ins => (
                  <div key={ins.id} className="p-3 flex items-center justify-between text-sm">
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold truncate">{ins.title}</p>
                      <p className="text-[11px] text-novatus-text-3">
                        {ins.type === "kb_snippet" ? "Team-Wissen" : `Persönlich · ${ins.target_user_name}`}
                        {" · "}
                        {new Date(ins.approved_at).toLocaleDateString("de-DE")}
                        {ins.approved_by_name && ` · ${ins.approved_by_name}`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <UserManageModal
        user={manageUser}
        currentUser={currentUser}
        onClose={() => setManageUser(null)}
        onSoftDelete={softDeleteUser}
        onRequestHardDelete={(u) => setHardDeleteFor(u)}
      />

      <HardDeleteModal
        user={hardDeleteFor}
        onCancel={() => setHardDeleteFor(null)}
        onConfirm={hardDeleteUser}
      />
    </div>
  );
};

export default Admin;
